import { forwardRef, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  Placement,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { isEqual } from 'lodash-es';
import { onlyText } from 'react-children-utilities';
import { useTranslation } from '@frontend/i18n';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  Button,
  CaretDownIconSmall,
  CheckboxField,
  ChecklistField,
  ChecklistMenuField,
  Chip,
  FieldLayout,
  FieldLayoutWithAction,
  ListFieldProps,
  PopoverMenu,
  SearchField,
  useControlledField,
  useOnClickOutside,
  usePopoverMenu,
} from '@frontend/design-system';

const FLOATING_MENU_MAX_HEIGHT = 400;
const FLOATING_MENU_MIN_WIDTH = 220;
const SEARCH_THRESHOLD = 10;

/**
 * Calculates the total number of selectable options across all groups
 */
function calculateTotalValuesCount(groups: GroupOption[]): number {
  return groups.reduce((count, group) => {
    return count + (group.children.length === 0 ? 1 : group.children.length);
  }, 0);
}

/**
 * Filters groups and their children based on search term.
 * Matches both group labels and child option labels.
 */
function filterGroupsBySearchTerm(groups: GroupOption[], searchTerm: string): GroupOption[] {
  if (!searchTerm) return groups;

  const lowerSearchTerm = searchTerm.toLowerCase();

  return groups
    .map((group) => {
      const groupLabelText = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
      const groupMatches = groupLabelText.toLowerCase().includes(lowerSearchTerm);

      if (group.children.length === 0) {
        return groupMatches ? group : null;
      }

      const filteredChildren = group.children.filter((child) => {
        const childLabelText = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
        return groupMatches || childLabelText.toLowerCase().includes(lowerSearchTerm);
      });

      if (filteredChildren.length === 0) return null;

      return { ...group, children: filteredChildren };
    })
    .filter((group): group is GroupOption => group !== null);
}

/**
 * Calculates selection states for all groups
 */
function calculateGroupSelectionStates<T extends string>(
  groups: GroupOption[],
  selectedValues: T[]
): Map<string, GroupSelectionState> {
  const states = new Map<string, GroupSelectionState>();

  groups.forEach((group) => {
    const hasChildren = group.children.length > 0;

    if (hasChildren) {
      const enabledChildren = group.children.filter((child) => !child.disabled);
      const enabledChildValues = enabledChildren.map((child) => child.value as T);
      const selectedCount = enabledChildValues.filter((childValue) => selectedValues.includes(childValue)).length;
      const totalCount = enabledChildValues.length;

      states.set(group.value, {
        allSelected: totalCount > 0 && selectedCount === totalCount,
        hasChildren: true,
        isIndeterminate: selectedCount > 0 && selectedCount < totalCount,
        selectedCount,
        totalCount,
      });
    } else {
      const isGroupSelected = selectedValues.includes(group.value as T);

      states.set(group.value, {
        allSelected: isGroupSelected,
        hasChildren: false,
        isIndeterminate: false,
        selectedCount: isGroupSelected ? 1 : 0,
        totalCount: 1,
      });
    }
  });

  return states;
}

/**
 * Gets all non-disabled selectable values from groups.
 */
function getAllSelectableValues<T extends string>(groups: GroupOption[]): T[] {
  const selectableValues: T[] = [];

  groups.forEach((group) => {
    if (group.children.length === 0) {
      selectableValues.push(group.value as T);
      return;
    }

    group.children.forEach((child) => {
      if (!child.disabled) {
        selectableValues.push(child.value as T);
      }
    });
  });

  const uniqueValues = new Set<string>();
  return selectableValues.filter((selectableValue) => {
    if (uniqueValues.has(selectableValue)) {
      return false;
    }
    uniqueValues.add(selectableValue);
    return true;
  });
}

/**
 * Creates a mapping of option values to their display labels.
 */
function createValueToLabelMap(groups: GroupOption[]): Record<string, string> {
  const map: Record<string, string> = {};

  groups.forEach((group) => {
    if (group.children.length === 0) {
      map[group.value] = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
    } else {
      group.children.forEach((child) => {
        map[child.value] = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
      });
    }
  });

  return map;
}

/**
 * Custom hook that manages search functionality for grouped checklists.
 */
function useGroupedChecklistSearch(groups: GroupOption[]) {
  const [searchTerm, setSearchTerm] = useState('');

  const totalValuesCount = useMemo(() => calculateTotalValuesCount(groups), [groups]);
  const showSearchBar = totalValuesCount > SEARCH_THRESHOLD;
  const filteredGroups = useMemo(() => filterGroupsBySearchTerm(groups, searchTerm), [groups, searchTerm]);

  const searchFieldProps = useControlledField({
    onChange: (newValue: string) => setSearchTerm(newValue),
    type: 'text',
    value: searchTerm,
  });

  const clearSearch = useCallback(() => setSearchTerm(''), []);

  return {
    searchTerm,
    setSearchTerm,
    showSearchBar,
    filteredGroups,
    searchFieldProps,
    clearSearch,
  };
}

const SearchBarField = memo(
  ({
    searchFieldProps,
    cssStyle,
  }: {
    searchFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
    cssStyle: SerializedStyles;
  }) => (
    <div css={cssStyle}>
      <SearchField
        {...searchFieldProps}
        name='search'
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
            return;
          }
          e.stopPropagation();
        }}
      />
    </div>
  )
);

SearchBarField.displayName = 'SearchBarField';

type GroupedChildOption = {
  disabled?: boolean;
  label: ReactNode;
  labelText?: string;
  value: string;
};

type GroupOption = {
  children: GroupedChildOption[];
  hideLabel?: boolean;
  labelText?: string;
  label: ReactNode;
  value: string;
};

type GroupedCheckListDropdownMenuProps<T extends string = string> = {
  disabled?: boolean;
  floatingStyleOverrides?: React.CSSProperties;
  groups: GroupOption[];
  label: string;
  name: string;
  onChange: (selected: T[]) => void;
  placement?: Placement;
  placeholder?: string;
  showLabel?: boolean;
  trackingIdBase?: string;
  value?: T[];
};

type GroupSelectionState = {
  allSelected: boolean;
  hasChildren: boolean;
  isIndeterminate: boolean;
  selectedCount: number;
  totalCount: number;
};

type GroupCheckboxProps = {
  disabled?: boolean;
  indeterminate: boolean;
  isChecked: boolean;
  label: ReactNode;
  name: string;
  onChange: (value: boolean) => void;
  trackingId?: string;
};

type ChipInputFieldWrapperProps = {
  keyToLabelMap: Record<string, string>;
  onRemoveClick: (chipValue: string) => void;
  selectedKeys: string[];
};

type ChipDropdownMenuCustomStyles = {
  applyButton?: CSSObject;
  checkListWrapper?: CSSObject;
  chipDropdownFilter?: CSSObject;
  popoverMenuWrapper?: CSSObject;
  topBar?: CSSObject;
};

type GroupedChecklistChipDropdownMenuProps<T extends string = string> = {
  customStyles?: ChipDropdownMenuCustomStyles;
  defaultValue?: T[];
  disabled?: boolean;
  groups: GroupOption[];
  label: string;
  onChange: (selected: T[]) => void;
  renderIcon?: (isFilterActive: boolean, isOpen?: boolean) => React.ReactNode;
  trackingId?: string;
  value?: T[];
};

const GroupCheckbox = memo(
  ({ disabled, indeterminate, isChecked, label, name, onChange, trackingId }: GroupCheckboxProps) => {
    const checkboxFieldProps = useControlledField({
      onChange: (newValue: boolean) => {
        onChange(newValue);
      },
      type: 'checkbox',
      value: isChecked,
    });

    return (
      <CheckboxField
        {...checkboxFieldProps}
        css={styles.groupCheckbox}
        disabled={disabled}
        indeterminate={indeterminate}
        label={label}
        labelPlacement='right'
        name={name}
        trackingId={trackingId}
      />
    );
  }
);

GroupCheckbox.displayName = 'GroupCheckbox';

const ChipInputFieldWrapper = memo(
  forwardRef<HTMLDivElement, ChipInputFieldWrapperProps>(
    ({ selectedKeys, keyToLabelMap, onRemoveClick, ...props }, ref) => {
      return (
        <div ref={ref} {...props} style={styles.chipInputContainer}>
          <div css={styles.chipInputWrapper}>
            <ChecklistMenuField.ChipInput
              keyToLabelMap={keyToLabelMap}
              onRemoveClick={onRemoveClick}
              selectedKeys={selectedKeys}
              variant='primary'
            />
          </div>
        </div>
      );
    }
  )
);

ChipInputFieldWrapper.displayName = 'ChipInputFieldWrapper';

const GroupedChecklistContent = memo(
  ({
    checklistProps,
    disabled,
    groups,
    groupStates,
    label,
    name,
    onToggleGroup,
    showLabel,
    trackingIdBase,
  }: {
    checklistProps: ListFieldProps;
    disabled?: boolean;
    groups: GroupOption[];
    groupStates: Map<string, GroupSelectionState>;
    label: string;
    name: string;
    onToggleGroup: (group: GroupOption) => void;
    showLabel?: boolean;
    trackingIdBase?: string;
  }) => {
    return (
      <ChecklistField
        {...checklistProps}
        css={styles.checklist(showLabel)}
        disabled={disabled}
        label={label}
        name={name}
      >
        {groups.map((group) => {
          const state = groupStates.get(group.value);
          const hasChildren = group.children.length > 0;
          const hideGroupLabel = group.hideLabel ?? false;

          return (
            <div css={styles.group} key={group.value}>
              {!hideGroupLabel && (
                <div css={styles.groupHeader(hasChildren)}>
                  <GroupCheckbox
                    disabled={disabled}
                    indeterminate={state?.isIndeterminate ?? false}
                    isChecked={state?.isIndeterminate ? true : state?.allSelected ?? false}
                    label={group.label}
                    name={group.value}
                    onChange={() => onToggleGroup(group)}
                    trackingId={`${trackingIdBase}-option-${group.value.toLowerCase()}`}
                  />
                </div>
              )}

              {hasChildren && (
                <>
                  {group.children.map((child) => (
                    <div css={styles.childOption(hideGroupLabel)} key={child.value}>
                      <ChecklistField.Option
                        css={styles.childCheckbox}
                        disabled={child.disabled}
                        name={child.value}
                        trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
                      >
                        {child.label}
                      </ChecklistField.Option>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })}
      </ChecklistField>
    );
  }
);

GroupedChecklistContent.displayName = 'GroupedChecklistContent';

export function GroupedCheckListDropdownMenu<T extends string = string>({
  disabled,
  floatingStyleOverrides,
  groups,
  label,
  name,
  onChange,
  placement = 'bottom',
  placeholder = 'Select one',
  showLabel = false,
  trackingIdBase,
  value = [],
}: GroupedCheckListDropdownMenuProps<T>) {
  const [menuState, setMenuState] = useState({
    isOpen: false,
  });

  const { showSearchBar, filteredGroups, searchFieldProps, clearSearch } = useGroupedChecklistSearch(groups);

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => {
      onChange?.(newValue as T[]);
    },
    type: 'checklist',
    value: value as string[],
  });

  const floatingNodeId = useFloatingNodeId();

  const floatingConfig = useMemo(
    () => ({
      middleware: [
        offset(placement === 'top' ? 12 : 3),
        flip(),
        size({
          apply({ elements, rects }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          },
        }),
      ],
      nodeId: floatingNodeId,
      onOpenChange: (nextOpen: boolean) => {
        setMenuState({ isOpen: nextOpen });
        if (!nextOpen) {
          clearSearch();
        }
      },
      open: menuState.isOpen,
      placement,
      whileElementsMounted: autoUpdate,
    }),
    [floatingNodeId, menuState.isOpen, placement, clearSearch]
  );

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>(floatingConfig);

  const { getReferenceProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  const groupSelectionStates = useMemo(() => calculateGroupSelectionStates(groups, value), [groups, value]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      if (!state?.hasChildren) {
        const parentValue = group.value as T;
        const isSelected = value.includes(parentValue);

        onChange(isSelected ? value.filter((v) => v !== parentValue) : [...value, parentValue]);
      } else {
        const enabledChildValues = group.children.filter((child) => !child.disabled).map((child) => child.value) as T[];

        if (state?.allSelected) {
          onChange(value.filter((v) => !enabledChildValues.includes(v)));
        } else {
          const updatedValue = [...value];
          enabledChildValues.forEach((childValue) => {
            if (!updatedValue.includes(childValue)) {
              updatedValue.push(childValue);
            }
          });
          onChange(updatedValue);
        }
      }
    },
    [groupSelectionStates, onChange, value]
  );

  const valueToLabelMap = useMemo(() => createValueToLabelMap(groups), [groups]);

  const handleRemoveChip = useCallback(
    ({ chipValue }: { chipValue: string }) => {
      onChange(value.filter((v) => v !== chipValue) as T[]);
    },
    [onChange, value]
  );

  const chipInputFieldProps = useMemo(
    () => ({
      keyToLabelMap: valueToLabelMap,
      onRemoveClick: (chipValue: string) => handleRemoveChip({ chipValue }),
      selectedKeys: value as string[],
    }),
    [handleRemoveChip, value, valueToLabelMap]
  );

  const handleToggleMenu = useCallback(() => {
    setMenuState((prevState) => ({ isOpen: !prevState.isOpen }));
  }, []);

  const floatingMenuStyles = useMemo(
    () => ({
      ...floatingStyles,
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.heavy,
      display: 'flex' as const,
      flexDirection: 'column' as const,
      maxHeight: FLOATING_MENU_MAX_HEIGHT,
      minWidth: FLOATING_MENU_MIN_WIDTH,
      zIndex: theme.zIndex.popover,
      ...floatingStyleOverrides,
    }),
    [floatingStyles, floatingStyleOverrides]
  );

  return (
    <FieldLayout
      {...checklistFieldProps}
      {...getReferenceProps()}
      containerCss={styles.fieldContainer}
      endAdornment={
        <CaretDownIconSmall
          color='subdued'
          css={[
            css`
              cursor: pointer;
              transition: transform 250ms ease-out;
              pointer-events: ${menuState.isOpen ? 'none' : 'auto'};
            `,

            menuState.isOpen &&
              css`
                transform: rotate(180deg);
              `,
          ]}
          name='alt-caret-down-tiny'
          onClick={handleToggleMenu}
          size={16}
        />
      }
      field={ChipInputFieldWrapper as unknown as ComponentProps<typeof FieldLayoutWithAction>['field']}
      fieldComponentProps={chipInputFieldProps}
      hasPadding={false}
      label={label}
      name={name}
      onChange={() => {}}
      placeholder={placeholder}
      ref={refs.setReference}
      value={value}
      active={menuState.isOpen}
    >
      <FloatingNode id={floatingNodeId}>
        {menuState.isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} initialFocus={0}>
              <div style={floatingMenuStyles} ref={refs.setFloating}>
                {showSearchBar && <SearchBarField searchFieldProps={searchFieldProps} cssStyle={styles.searchBar} />}
                <div css={styles.floatingScrollableContent}>
                  <GroupedChecklistContent
                    checklistProps={checklistFieldProps}
                    disabled={disabled}
                    groups={filteredGroups}
                    groupStates={groupSelectionStates}
                    label={label}
                    name={name}
                    onToggleGroup={handleToggleGroupSelection}
                    showLabel={showLabel}
                    trackingIdBase={trackingIdBase}
                  />
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </FieldLayout>
  );
}

export function GroupedChecklistChipDropdownMenu<T extends string = string>({
  customStyles,
  defaultValue = [],
  disabled,
  groups,
  label,
  onChange,
  renderIcon,
  trackingId,
  value,
}: GroupedChecklistChipDropdownMenuProps<T>) {
  const { t } = useTranslation('call-intelligence');
  const { close, getMenuProps, getTriggerProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-start',
  });
  const { ref: filterTriggerRef, ...triggerProps } = getTriggerProps();
  const [internalSelection, setInternalSelection] = useState<T[]>(value || []);

  const { showSearchBar, filteredGroups, searchFieldProps, clearSearch } = useGroupedChecklistSearch(groups);

  const outsideClickRef = useRef<HTMLDivElement>(null);
  const isFilterActive = !isEqual([...defaultValue].sort(), [...(value || [])].sort());

  const leftElement = useMemo(() => {
    return renderIcon ? renderIcon(isFilterActive, isOpen) : null;
  }, [renderIcon, isFilterActive, isOpen]);

  useEffect(() => {
    setInternalSelection(value || []);
  }, [value]);

  useEffect(() => {
    if (!isOpen) {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  const handleCancelSelection = useCallback(() => {
    setInternalSelection(value || []);
    close();
  }, [close, value]);

  useOnClickOutside({
    handler: handleCancelSelection,
    ref: outsideClickRef,
  });

  const handleApplySelection = useCallback(() => {
    onChange(internalSelection);
    close();
  }, [close, internalSelection, onChange]);

  const groupSelectionStates = useMemo(
    () => calculateGroupSelectionStates(groups, internalSelection),
    [groups, internalSelection]
  );

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      if (!state?.hasChildren) {
        const groupValue = group.value as T;
        setInternalSelection((previousSelection) =>
          previousSelection.includes(groupValue)
            ? previousSelection.filter((selectedValue) => selectedValue !== groupValue)
            : [...previousSelection, groupValue]
        );
        return;
      }

      const enabledChildren = group.children.filter((child) => !child.disabled).map((child) => child.value as T);

      setInternalSelection((previousSelection) => {
        if (state.allSelected) {
          return previousSelection.filter((selectedValue) => !enabledChildren.includes(selectedValue));
        }

        const updatedSelection = [...previousSelection];
        enabledChildren.forEach((childValue) => {
          if (!updatedSelection.includes(childValue)) {
            updatedSelection.push(childValue);
          }
        });
        return updatedSelection;
      });
    },
    [groupSelectionStates]
  );

  const allSelectableValues = useMemo(() => getAllSelectableValues<T>(groups), [groups]);

  const handleSelectAll = useCallback(() => {
    setInternalSelection(allSelectableValues);
  }, [allSelectableValues]);

  const handleClearAll = useCallback(() => {
    setInternalSelection([]);
  }, []);

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => setInternalSelection(newValue as T[]),
    type: 'checklist',
    value: internalSelection as string[],
  });

  return (
    <div ref={filterTriggerRef}>
      <Chip.DropdownFilter
        {...triggerProps}
        css={{
          gap: theme.spacing(1),
          maxWidth: 'none',
          ...customStyles?.chipDropdownFilter,
        }}
        filterIsActive={isFilterActive}
        leftElement={leftElement}
        onClick={open}
        trackingId={trackingId}
      >
        {label}
      </Chip.DropdownFilter>

      <PopoverMenu {...getMenuProps()}>
        <div css={[styles.checkListWrapper, customStyles?.popoverMenuWrapper]} ref={outsideClickRef}>
          <div css={[styles.topBar, customStyles?.topBar]}>
            <Button onClick={handleSelectAll} variant='tertiary' disabled={disabled}>
              {t('Select all')}
            </Button>
            <Button onClick={handleClearAll} variant='tertiary' disabled={disabled}>
              {t('Clear')}
            </Button>
          </div>

          {showSearchBar && <SearchBarField searchFieldProps={searchFieldProps} cssStyle={styles.searchBar} />}

          <div css={[styles.scrollableContent, customStyles?.checkListWrapper]}>
            <GroupedChecklistContent
              checklistProps={checklistFieldProps}
              disabled={disabled}
              groups={filteredGroups}
              groupStates={groupSelectionStates}
              label={label}
              name={label}
              onToggleGroup={handleToggleGroupSelection}
              showLabel={false}
              trackingIdBase={trackingId}
            />
          </div>

          <Button
            css={[styles.applyButton, customStyles?.applyButton]}
            disabled={disabled}
            onClick={handleApplySelection}
            trackingId={trackingId ? `${trackingId}-apply-button` : undefined}
          >
            {t('Apply')}
          </Button>
        </div>
      </PopoverMenu>
    </div>
  );
}

const styles = {
  checklist: (showLabel?: boolean) => css`
    > label {
      display: ${showLabel ? 'block' : 'none'};
    }

    > div {
      align-items: center;
    }

    > div:last-of-type {
      margin-bottom: 0;
    }
  `,

  childCheckbox: css`
    margin: 0;
  `,

  childOption: (hideGroupLabel?: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${hideGroupLabel ? theme.spacing(1, 0) : theme.spacing(1, 1, 1, 2)};

    > div {
      width: 100%;

      > label {
        width: 100%;
      }
    }
  `,

  group: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(1, 2)};
    &:last-of-type {
      border-bottom: none;
    }
  `,

  groupCheckbox: css`
    margin: 0;
    align-items: center;
    width: 100%;
    > label {
      width: 100%;
    }
  `,

  groupCheckboxOption: css`
    margin: 0;
    > label {
      display: none;
    }
  `,

  groupHeader: (hasChildren: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(hasChildren ? 1 : 0)};
  `,

  chipInputContainer: {
    height: '100%',
    width: '100%',
  },

  chipInputWrapper: css`
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    .chip {
      background-color: ${theme.colors.primary5};
      color: ${theme.font.colors.default};

      svg {
        color: ${theme.colors.primary90};
      }
    }
  `,

  fieldContainer: css`
    > div {
      height: 40px;
      padding: ${theme.spacing(1, 1)};
    }
  `,
  /******* */

  floatingScrollableContent: css`
    overflow: auto;
    flex: 1;
  `,
  searchBar: css`
    padding: ${theme.spacing(1, 2)};
  `,
  topBar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(0.25, 2)};

    button {
      color: ${theme.colors.text.interactive};
    }
  `,
  checkListWrapper: css`
    display: flex;
    flex-direction: column;
    min-width: 220px;
  `,
  scrollableContent: css`
    overflow: auto;
    max-height: 400px;
    flex: 1;
  `,
  applyButton: css`
    margin: auto;
    width: calc(100% - ${theme.spacing(2)});
  `,
};
